import React, { useReducer, useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { cleanRef, ensureBoolean, isEmpty, isNotEmpty, UCFirst } from '../../lib/ep';

import Checkbox, { Checkbox as NonSwitcherCheckbox } from './Checkbox';
import ButtonCollection from './ButtonCollection';
import DateWithSelector from './DateWithSelector';
import DynamicFields from './DynamicFields';
import FakeFormField from './FakeFormField';
import FloatingLayer from './FloatingLayer';
import FileUploader, { AutoPostFileUploader } from './FileUploader';
import Input from './Input';
import Model from './Model';
import NavigationTabs from './NavigationTabs';
import SaveButton from './SaveButton';
import Select from './Select';
import TextArea from './TextArea';
import Tooltip from './Tooltip';
import Autocomplete from './Autocomplete';
import RadioGroup from './RadioGroup';
import SignatureCanvas from './SignatureCanvas';
import { SquaredButton } from './ActionButton';
import KeyPair from './KeyPair';
import CheckBoxGroup from './CheckBoxGroup';


const sizes_file = [
	{ k:'1', v:"1 MB" },
	{ k:'2', v:"2 MB" },
	{ k:'3', v:"3 MB" },
	{ k:'4', v:"4 MB" },
	{ k:'5', v:"5 MB" },
	{ k:'6', v:"6 MB" },
	{ k:'7', v:"7 MB" },
	{ k:'8', v:"8 MB" },
	{ k:'9', v:"9 MB" },
	{ k:'10', v:"10 MB" },
];

const default_field_types = [{
	type:'field',
	icon:'knk-text',
	label:"Additional text field",
	translate:[{
		key:'title',
		type:'input',
	}],
}, {
	type:'dropdown',
	icon:'knk-options-list',
	label:"Drop-down list",
	translate:[{
		key:'title',
		type:'input',
	}, {
		key:'values',
		type:'textarea',
	}],
}, {
	type:'autocomplete',
	icon:'knk-prediction',
	label:"Autocomplete",
	translate:[{
		key:'title',
		type:'input',
	}, {
		key:'values',
		type:'textarea',
	}],
}, {
	type:'checkbox',
	icon:'knk-ok',
	label:"Opt-in field",
	translate:[{
		key:'title',
		type:'input',
	}],
}, {
	type:'multi_checkbox',
	icon:'knk-multichoice',
	label:"Multi option",
	translate:[{
		key:'title',
		type:'input',
	}],
}, {
	type:'radio_button',
	icon:'knk-radiobutton-list',
	label:"Radio button",
	translate:[{
		key:'title',
		type:'input',
	},{
		key:'values',
		type:'textarea',
	}],
}, {
	type:'date',
	icon:'knk-calendar',
	label:"Date field",
	translate:[{
		key:'title',
		type:'input',
	}],
}, {
	type:'attach',
	icon:'knk-attachment',
	label:"Documents field",
	translate:[{
		key:'title',
		type:'input',
	}],
}, {
	type:'signature',
	icon:'knk-signature',
	label:"Signature field",
	translate:[{
		key:'title',
		type:'input',
	}],
}];

default_field_types.map(type=>{
	type.translate.push({
		label:'Help message',
		key:'after_message',
		type:'input'
	})
});

const TranslateField = ({ field, loc, dispatch, ...props }) => {
	const field_localized = (field.loc !== undefined && field.loc[loc] != undefined) ? field.loc[loc] : {};

	const field_title = (field_localized.title != undefined) ? field_localized.title : "";
	const field_values = (field_localized.values != undefined) ? field_localized.values : "";

	let total_items_to_translate = 1;
	let total_items_translated = (field_title != "") ? 1 : 0;
	switch(field.type) {
		case 'dropdown':
		case 'select':
			total_items_to_translate += 1;
			if(field_values != "") total_items_translated++;
			break;
	}

	// TO DO: si tenim moltes opcions al SELECT, fer el textarea "v_size:'big'". Si no, fer el textarea "v_size:'normal'". I adaptar el max-height CSS del llistat .gui_fake_options en conseqüència
	let status_icon_class = ['is_field_status', 'gui_small_icon'];
	status_icon_class.push((total_items_translated > 0) ? 'ep-check-list-item' : 'ep-delete-circle');
	if(total_items_to_translate == total_items_translated) {
		status_icon_class.push('greenIcon');
	} else if(total_items_translated > 0) {
		status_icon_class.push('blueIcon');
	} else {
		status_icon_class.push('redIcon');
	}

	return (
		<>
			<div class="is_preview">
				<FakeFormField field={field} />
			</div>

			<Model key={`fields_${loc}`} classname="is_form gui_is_animated" model={field_localized} change={(k, v) => dispatch({ type:'TRANSLATE', id:field.id, loc, k, v })}>
				<Input name="title" value={field_title}>Field label</Input>

				{(field.type == 'dropdown' || field.type == 'select') && (
					<TextArea key={`${field.id}_${loc}`} _class={{v_size:'big'}} name="values">Options</TextArea>
				)}
			</Model>

			<span class={status_icon_class.join(" ")} />
		</>
	)
};

const Field = ({ model, ...props }) => {
	const enable_public_value = props?.enable_public_value ?? [];

	const skip_attach = props?.skip_attach ?? [];

	let type_attach = [
		{ k:'text', v:"Any text file" },
		{ k:'image', v:"Any picture file" },
		{ k:'audio', v:"Any audio file" },
		{ k:'application', v:"PDF only" },
		{ k:'all', v:"All types" },
	];

	type_attach = type_attach.filter((a) => (!skip_attach.includes(a.k)));

	const change = (k,v) => {
		if(k == 'title') {
			if(model.short_title == undefined || model.short_title == "" || model.short_title == cleanRef(model.title)) {
				props.change('short_title',cleanRef(v));
			}
		}
		if (k == 'mandatory') {
			if (model.type == 'multi_checkbox') {
				if (isEmpty(model, 'min_answers')){
					props.change('min_answers', 1);
				}
			}
		}
		props.change(k,v);
	}
	const errors = props.errors[model.id];

	const fields_with_mandatory = ['field', 'checkbox', 'signature','multi_checkbox'];

	return (
		<Model model={model} change={change} errors={errors}>
			<Input
				name="title"
				_class={{ size:60 }}
			>Title</Input>

			<Input
				name="short_title"
				_class={{ size:60 }}
				label_props={{
					tooltip:<Tooltip>A short label that helps you identify this field for internal use. We recommend 1 word.</Tooltip>,
				}}
			>Reference</Input>

			{(model.type == 'field') && (
				<Select
					_class={{ size:50 }}
					name="validation"
					values={[
						{ k:0, v:"No validation" },
						{ k:1, v:"By a list of closed values" },
						{ k:2, v:"By a regular expression" },
					]}
				>Method of validation</Select>
			)}

			{(model.type == 'field' && model.validation == 1) && (
				<TextArea
					name="valid_values"
				>Values</TextArea>
			)}

			{(model.type == 'field' && model.validation == 2) && (
				<Input
					name="regexp"
				>Validaton Regexp</Input>
			)}

			{model.type == 'multi_checkbox' && (
				<DynamicFields
					section_title={`${("Options list")} (${model?.values?.length ?? 0})`}
					section_icon={false}
					inline={true}
					can_copy={false}
					field_types={[{
						type:'kv_pair',
						label:"Add option",
					}]}
					fields={model?.values ?? []}
					onChange={(v)=>change('values',v)}
					section_classes='gui_not_in_top'
					ul_classes='gui_overflowable'
					ul_styles={{ maxHeight:'230px' }}
					header={<div class="keyvalue_header">
						<strong>{("Values")}</strong>

						<strong>{("Keys")}</strong>
					</div>}
					>
					<KeyPair infer_key={true} />
				</DynamicFields>
			)}

			{(fields_with_mandatory.includes(model.type)) && (
				<Checkbox
					name="mandatory"
				>Is this field mandatory?</Checkbox>
			)}

			{(model.type == 'attach') && (
				<Checkbox
					name="mandatory"
					value={ensureBoolean(model?.mandatory ?? false)}
				>Is this field mandatory?</Checkbox>
			)}

			{(model.type == 'field') && (
				<Checkbox
					name="large"
				>Large Text</Checkbox>
			)}

			{(model.type == 'dropdown' || model.type == 'autocomplete' || model.type == 'radio_button') && (
				<TextArea
					name="values"
				>Values</TextArea>
			)}

			{(model.mandatory && model.type == 'multi_checkbox') && (
				<Input
					name="min_answers"
					_class={{ size:60 }}
					label_props={{
						tooltip:<Tooltip>Min values to be selected</Tooltip>,
					}}
				>Min selected</Input>
			)}

			{(model.mandatory && model.type == 'multi_checkbox') && (
				<Input
					name="max_answers"
					_class={{ size:60 }}
					label_props={{
						tooltip:<Tooltip>Max values to be selected</Tooltip>,
					}}
				>Max selected</Input>
			)}

			{(model.type == 'checkbox') && (
				<Checkbox
					name="checked"
				>Checked by default</Checkbox>
			)}

			{(model.type == 'date') && (
				<Input
					name="min_year"
					_class={{ size:60 }}
					label_props={{
						tooltip:<Tooltip>Enter the minimum year to be displayed in the year selector</Tooltip>,
					}}
				>Min. year</Input>
			)}

			{(model.type == 'date') && (
				<Input
					name="max_year"
					_class={{ size:60 }}
					label_props={{
						tooltip:<Tooltip>Enter the maximum year to be displayed in the year selector</Tooltip>,
					}}
				>Max. year</Input>
			)}

			{(model.type == 'attach') && (
				<Select
					name="mediatype"
					_class={{ size:50 }}
					values={type_attach}
				>Document type</Select>
			)}

			{(model.type == 'attach') && (
				<Select
					name="size"
					_class={{ size:30 }}
					values={sizes_file}
				>Maximum file size</Select>
			)}

			{(enable_public_value.includes(model.type)) && (
				<Checkbox
					name="public"
					label_props={{
						tooltip:<Tooltip>If you make this field public, the value entered by the participant will be visible in the contest gallery, and anyone will be able to see it. If you keep it private, the value entered will only be visible to the contest administrator from the participants management tool.</Tooltip>
					}}
				>Public field</Checkbox>
			)}

			<Input
				name="after_message"
				_class={{ size:60 }}
			>Help message</Input>
		</Model>
	)
};

const LocalizeField = (field, loc) => {
	if(loc == undefined) return field;
	if(field.loc == undefined) return field;
	if(field.loc[loc] == undefined) return field;
	return { ...field, ...field.loc[loc] };
};

export const hydrateForm = (form, data) => {
	let info = [];
	let index_form = {};
	for(let i in form) { index_form[form[i].id] = form[i]};

	for(let j in data) {
		let d = data[j];
		if(index_form[j] && index_form[j].type == 'checkbox') d = (d == 'true') ? "Checked" : "Unchecked";
		if(index_form[j] == undefined) continue;
		info.push({ ...index_form[j], value:d });
	}

	return info;
};

export const fullHydrateForm = (form, data) => {
	const info = [];
	const index_form = {};
	for(let i in form) { index_form[form[i].id] = form[i]};

	for(let j in data) {
		if(index_form[j] == undefined) continue;

		let d = data[j];
		if(index_form[j] && index_form[j].type == 'checkbox') d = (d == 'true') ? 'Marcado' : 'No Marcado';
		info.push({...index_form[j], value:d});
	}

	return info;
};

export const DynamicFormBuilder = ({ form, onChange, ...props }) => {
	const langs = ['cat', 'eng'];

	const [ state, dispatch ] = useReducer((state, action) => {
		switch(action.type) {
			case 'TOGGLE_TRANSLATE':
				return { ...state, translating:!state.translating };

			case 'TRANSLATE_LANG':
				return { ...state, actual_lang:action.lang };

			case 'TRANSLATE':
				return { ...state, form:state.form.map((field) => {
					if(field.id != action.id) return field;
					if(field.loc == undefined ) field.loc = {};
					if(field.loc[action.loc] == undefined) field.loc[action.loc] = {};
					if(action.v == '' && field.loc[action.loc][action.k] != undefined) {
						delete field.loc[action.loc][action.k];
					} else {
						field.loc[action.loc][action.k] = action.v;
					}
					return {...field};
				})};

			case 'CLEAR_ERRORS':
				return { ...state,errors:false };

			case 'SET_ERROR':
				if(state.errors === false) state.errors = {};
				if(state.errors[action.field_id] == undefined) state.errors[action.field_id] = {};
				state.errors[action.field_id][action.field] = action.error;				

				return {
					...state,
					errors:{...state.errors}
				}

			default:
				return state;
		}
	}, {
		form,
		actual_lang:langs[0],
		translating:false,
		errors:false
	});

	const only = props?.only ?? ['field', 'dropdown', 'checkbox', 'date', 'attach','multi_checkbox'];
	const skip = props?.skip ?? [];
	const selectLang = (lang) => {
		dispatch({ type:'TRANSLATE_LANG', lang });
	};

	const saveTranslations = (cb) => {
		dispatch({ type:'TOGGLE_TRANSLATE' });
		onChange(state.form);
		if(cb != undefined) cb();
	};

	let sections = [];
	langs.map((lang) => {
		sections.push({
			label:lang,
			onClick:() => selectLang(lang),
			selected:(state.actual_lang == lang) ? true : false,
		});
	});

	let field_types = [...default_field_types];

	if(skip.length > 0) {
		field_types = field_types.filter((field) => { return skip.indexOf(field.type) == -1;})
	}

	if(only.length > 0) {
		field_types = field_types.filter((field) => { return only.indexOf(field.type)  > -1;})
	}

	const onChangeWithValidation = (form) => {
		const unique_ref = {};
		if(state.errors != false) dispatch({ type:'CLEAR_ERRORS' });

		form.map((field) => {
			//Common errors
			if(!isNotEmpty(field, 'title')) {
				dispatch({
					type:'SET_ERROR',
					field_id:field.id,
					field:"title",
					error:"Title is mandatory",
				});
			}

			if(field.type == 'dropdown') {
				const values = field.values != undefined ? field.values.split('\n') : [];
				let _final_values = {};
				let repeated_values = false;
				let _total_values = 0;
				values.forEach(element => {
					if(element.length == 0 || repeated_values)return;
					const _indexed_name = element.toLowerCase();
					if(_final_values[_indexed_name] != undefined)repeated_values = true;
					_final_values[_indexed_name] = 1;
					_total_values++;
				});

				if(repeated_values) {
					dispatch({
						type:'SET_ERROR',
						field_id:field.id,
						field:"values",
						error:"Repeated values",
					});
				} else if(_total_values <= 1) {
					dispatch({
						type:'SET_ERROR',
						field_id:field.id,
						field:"values",
						error:"Invalid or insufficient values",
					});
				}
			}

			if(field.type == 'date') {
				if(!field.max_year) {
					dispatch({
						type:'SET_ERROR',
						field_id:field.id,
						field:"max_year",
						error:"Invalid value",
					});
				}

				if(!field.min_year) {
					dispatch({
						type:'SET_ERROR',
						field_id:field.id,
						field:"min_year",
						error:"Invalid value",
					});
				}
			}

			if(field.short_title == undefined || field.short_title == '') return;

			if(unique_ref[field.short_title] != undefined) {
				dispatch({
					type:'SET_ERROR',
					field_id:field.id,
					field:"short_title",
					error:"Reference is already in use in this form",
				});
				return;
			}

			unique_ref[field.short_title] = 1;
		});

		onChange(form);
	};

	return (
		<>
			<DynamicFields
				section_title={props?.main_label ?? "Fields"}
				fields={form}
				errors={state.errors}
				field_types={field_types}
				onChange={onChangeWithValidation}
				translate_field={<TranslateField />}
				{...props}
			>
				<Field
					langs={langs}
					skip_attach={props.skip_attach}
					enable_public_value={props.enable_public_value}
				/>
			</DynamicFields>

			{(state.translating) && (
				<FloatingLayer _class={{size:'small'}} backButton={() => dispatch({ type:'TOGGLE_TRANSLATE' })}>
					<div class="tabs_in_layer">
						Translate
						<NavigationTabs
							active={state.actual_lang}
							sections={sections}
							display_type='tab'
							in_frontend={true}
							_class={{ other:'gui_not_in_top_huge' }}
							children_class="gui_is_not_padded"
						/>

						<ul class="gui_overflowable list_with_tabs">
							{state.form.map((field, f) => {
								return (
									<li key={`field_${f}`}>
										{langs.map((loc) => {
											if(loc != state.actual_lang) return null;

											const field_title = (field.loc !== undefined && field.loc[loc] != undefined) ? field.loc[loc].title : "";
											const field_values = (field.loc !== undefined && field.loc[loc] != undefined) ? field.loc[loc].values : "";

											return (
												<Model key={`fields_${f}_${loc}`} model={field} change={(k, v) => dispatch({ type:'TRANSLATE', id:field.id, loc, k, v })}>
													<Input
														name="title"
														value={field_title}
													>{field.title}</Input>

													{(field.type == 'dropdown') && (
														<TextArea
															name="values"
															value={field_values}
															label_props={{
																autotranslate:false,
															}}
														>Values: {field.values}</TextArea>
													)}
												</Model>
											)
										})}
									</li>
								)
							})}
						</ul>

						<ButtonCollection in_floating={true}>
							<SaveButton
								onClick={(e, cb) => saveTranslations(cb)}
							>Save translations</SaveButton>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</>
	)
};

export const DisplayDynamicForm = ({ form, values, ...props }) => {
	const hydrated = fullHydrateForm(form, values);
	const is_builder = props?.is_builder ?? false;

	const getIcon = (type) => {
		const field_type = default_field_types.find((f) => (f.type == type));
		return field_type.icon;
	};

	const getFileExtension = (filename) => {
		const extension = filename.split('.').pop();
		return extension.toLowerCase();
	};

	const getFileName = (filename) => {
		const parts = filename.split('/');
		return parts[parts.length - 1];
	};

	const fieldValue = (field) => {
		switch(field.type) {
			case 'signature':
				return (
					<div class="file_thumbnail">
						<img src={field.value} height={50} alt="" />

						{(is_builder) && (
							<ButtonCollection>
								<SquaredButton
									icon='knk-download'
									_target='_blank'
									_href={field.value}
									aria-label="Download signature"
								/>
							</ButtonCollection>
						)}
					</div>
				);

			case 'attach':
				const file_type = getFileExtension(field.value);
				const is_image = (['jpg', 'jpeg', 'gif', 'png'].includes(file_type)) ? true : false;

				return (
					<div class="file_thumbnail">
						{(is_image) ? (
							<img src={field.value} height={50} alt="" />
						) : (
							<span>{getFileName(field.value)}</span>
						)}

						{(is_builder) && (
							<ButtonCollection>
								<SquaredButton
									icon='knk-download'
									_target='_blank'
									_href={field.value}
									aria-label="Download signature"
								/>
							</ButtonCollection>
						)}
					</div>
				);

			case 'date':
				if(isNotEmpty(props, 'date_format')) return EPDateFormat(field.value, props.date_format);
				return field.value;


			case 'multi_checkbox':
				if(isEmpty(field, 'value')) return field.value;
				//console.log(field);
				//const values = JSON.parse(field.value);
				const values = field.value;

				return (
					<ul class="bulleted_list">
						{values.map((value, v) => {
							return (
								<li key={`answer_${v}`}>{field.values.find((f) => f.k == value).v}</li>
							)
						})}
					</ul>
				)

			default:
				return field.value;
		}
	};

	const classes = ['answered_data'];
	if((props?.not_margined ?? false) === false) classes.push('gui_not_in_top_small');

	const humanize_title = props?.humanize_title ?? false;

	return (
		<dl class={classes.join(" ")}>
			{hydrated.map((field, f) => {
				if(!is_builder && ['attach'].includes(field.type)) return null;

				let title = field?.short_title ?? field.title;
				if(humanize_title) title = UCFirst(title.replace(/\_/gi, " "));

				return (
					<div key={`field_${f}`}>
						<dt class={`cutTextOnOverflow ${getIcon(field.type)}`}>{title}</dt>

						<dd>{fieldValue(field)}</dd>
					</div>
				)
			})}
		</dl>
	)
};

export const validateDynamicForm = (form, values) => {
	const errors = {};
	const mandatory_fields = ['dropdown', 'radio_button'];
	form.map((field) => {
		const user_value = values[field.id];
		if(field.validation != undefined && field.validation != 0 && (values[field.id] != undefined && values[field.id] != "")) {
			switch (field.validation) {
				case "1":
					if(!(field?.valid_values ?? '').split('\n').includes(user_value)) {
						errors[field.id] = "Invalid value";
					}
					break;

				case "2":
					try {
						const regexp = new RegExp(field.regexp,'i');
						if(!regexp.test(user_value)) {
							errors[field.id] = "Invalid value";
						}
					} catch (error) {
						errors[field.id] = "Invalid value";
					}
					break;
			}
		}

		if(!mandatory_fields.includes(field.type) && !ensureBoolean((field?.mandatory ?? false))) return;
	
		if(values[field.id] != undefined && values[field.id] != "") return;
		if(field.short_title != undefined && values[field.short_title] != undefined && values[field.short_title] != "") return;

		errors[field.id] = "This field cannot be empty";
	})

	return errors;
}

export const DynamicForm = injectIntl(({ form, ...props }) => {
	const [ user, setUser ] = useState(props?.user ?? {});
	const [ errors, setErrors ] = useState(props?.errors ?? {});

	const change = (k, v) => {
		const is_full = k.indexOf('full_') > -1;
		const key = k.replace('full_','');
		const field = form.find((f) => f.id == key);

		if(field && field.type == 'attach' && v != null) {
			if(!props?.field_config?.[field.type]?.auto_url) {
				fileUpload(v, field);
				return;
			}
		}

		if(field && field.type == 'date' && v != null) {
			//v = `${v.year}-${v.month}-${v.day} 00:00:00`;
			v = `${v.year}-${v.month}-${v.day}`;
		}

		if(field && field.type == 'multi_checkbox' && v != null) {
			v = JSON.parse(v);
			//console.log(v);
		}

		setUser((u) => {
			const data = { ...u, [key]:v };

			if(field.short_title) {
				// data[`${is_full?'full_':''}${field.short_title}`]=v;
				data[field.short_title]=v;
			}
			return data;
		});
	}

	 useEffect(() => {
		const userInit = props.user ?? {};

		form.map((field) => {
			if(userInit == false) return;
			if(!isEmpty(userInit, field.id)) return;
			if(field.type != 'checkbox') return;
			userInit[field.id] = field?.checked ?? false;

			if(field.short_title) {
				userInit[field.short_title] = field?.checked ?? false;
			}
		});

		if(JSON.stringify(props.user) === JSON.stringify(user)) {
			return;
		}
		setUser(userInit);
	}, [ props.user ]);

	const validate = () => {
		let errors = {};

		form.map((field) => {
			switch(field.type) {
				case 'field':
				case 'checkbox':
				case 'dropdown':
				case 'signature':
				case 'radio_button':
				case 'attach':
					if(ensureBoolean(field?.mandatory ?? false) && (user[field.id] == undefined || user[field.id] == "")) {
						errors[field.id] = "This field is mandatory";
					}
					break;

				case 'date':
					if(user[field.id] == undefined || user[field.id] == "") {
						errors[field.id] = "This field is mandatory";
					}
					break;

				case 'multi_checkbox':
					if(field?.mandatory) {
						if(user[field.id] == undefined) {
							errors[field.id] = "This field is mandatory";
						} else {
							//const answers = JSON.parse(user[field.id]);
							const answers = user[field.id];
							const min_answers = field?.min_answers ?? 0;
							const max_answers = field?.max_answers ?? 100;

							let error_msg;
							if(answers.length < min_answers) {
								error_msg = (max_answers > 1) ? "A minimum of {num} options must be selected" : "A minimum of {num} option must be selected";
								errors[field.id] = props.intl.formatMessage({ id:error_msg }, { num:min_answers });
							} else if(answers.length > max_answers) {
								error_msg = (max_answers > 1) ? "A maximum of {num} options can be selected" : "A maximum of {num} option can be selected";
								errors[field.id] = props.intl.formatMessage({ id:error_msg }, { num:max_answers });
							}
						}
					}

					break;
			}
		});

		const errors_2 = validateDynamicForm(form,user);
		const joined_errors = { ...errors, ...errors_2 };
		setErrors(joined_errors);

		return (Object.keys(joined_errors).length == 0) ? true : false;
	};

	useEffect(() => {
		const is_valid = validate();
		if(props.onChange != undefined) props.onChange(user, is_valid);
	}, [ user ]);

	const show_errors = props?.showFormErrors ?? true;
	if(form.length == 0 || Object.keys(form).length == 0) return null;

	const fileUpload = (base64, field) => {
		if(props.fileUpload != undefined) props.fileUpload(base64, field, (result) => {
			setUser((u) => {
				const data = {...u,[field.id]:result.full}
				if(field.short_title) {
					data[field.short_title] = result.full;
				}
				return data;
			});
		});
	};

	const public_form = props?.public_form ?? false;

	return (
		<Model
			classname={props.model_classes}
			model={user}
			change={change}
			errors={(show_errors) ? errors : {}}
			use_wrapper={props.use_wrapper}
		>
			{form.map((original_item, i) => {
				const item = LocalizeField(original_item, props.loc);

				const mandatory = ensureBoolean(item?.mandatory ?? ((['dropdown', 'date', 'radio_button'].includes(item.type)) ? true : false));

				let field_props = {
					key:item.id,
					name:item.id,
					is_builder:props.is_builder,
					after_message:item.after_message,
					label_props:{
						mandatory,
					},
				};

				if(i == 0) {
					field_props._class = { in_top:true };
				}

				if(props.extra_field_props != undefined) {
					field_props = { ...field_props, ...props.extra_field_props };
				}

				if(props?.field_config?.[item.type]) {
					field_props = { ...field_props, ...props?.field_config?.[item.type] };
				}

				switch(item.type) {
					case 'field':
						let _large_field = item?.large && item.large != "false" && item.large != "0" ? true : false; 
						if(_large_field) {
							return (
								<TextArea
									{...field_props}
								>{item.title}</TextArea>
							)
						}

						return (
							<Input
								{...field_props}
							>{item.title}</Input>
						);

					case 'dropdown':
						let values = [{ k:'', v:'---' }];
						values = (item.values != undefined) ? values.concat(item.values.split("\n").filter((item) => { return item != ''}).map((i) => ({ k:i, v:i }))) : values;

						return (
							<Select
								values={values}
								{...field_props}
							>{item.title}</Select>
						);

					case 'autocomplete':
						const auto_complete_values = (item?.values != undefined ? item.values.split("\n") : []).map((v) => ({ k:v, v:v }));
						let fetchvalues = (v, cb) => {
							let vs = auto_complete_values.filter((o) => {
								const reg = new RegExp(v.value, 'gi');
								return reg.test(o.k);
							});

							cb(vs.map((v) => ({ value:v.k })));

							return vs;
						};

						return (
							<Autocomplete
								values={auto_complete_values}
								fetch={fetchvalues}
								{...field_props} 
								selected={(v) => change(item.id, v.suggestion.value)}
							>{item.title}</Autocomplete>
						);

					case 'checkbox':
						const check_status = (user[item.id] == undefined) ? (item.checked != undefined && item.checked) : user[item.id];
						const ComponentCheckbox = (public_form) ? NonSwitcherCheckbox : Checkbox;
						return (
							<ComponentCheckbox
								value={check_status}
								{...field_props}
							>{item.title}</ComponentCheckbox>
						);

					case 'radio_button':
						const radio_values = (item.values != undefined) ? [].concat(item.values.split("\n").filter((item) => { return item != ''}).map((i) => ({ label:i, value:i }))) : [];
						return (
							<RadioGroup
								elements={radio_values}
								{...field_props}
							>{item.title}</RadioGroup>
						);

					case 'date':
						return (
							<DateWithSelector
								min_year={item.min_year}
								max_year={item.max_year}
								{...field_props}
							>{item.title}</DateWithSelector>
						);

					case 'attach':
						let accepted_formats = [];
						let custom_upload_msg = "Upload the file";

						switch(item.mediatype) {
							case 'text':
								accepted_formats = ['DOC', 'TXT'];
								custom_upload_msg = "Upload the text file";
								break;

							case 'image':
								accepted_formats = ['JPEG', 'PNG', 'GIF'];
								custom_upload_msg = "Upload the image file";
								break;

							case 'audio':
								accepted_formats = ['MP3'];
								custom_upload_msg = "Upload the audio file";
								break;

							case 'application':
								accepted_formats = ['PDF'];
								custom_upload_msg = "Upload the PDF image";
								break;

							case 'all':
							default:
								accepted_formats = ['JPEG', 'PNG', 'GIF', 'MP3', 'PDF', 'DOC', 'TXT'];
								break;
						}

						field_props.file = (user[item.id] != undefined) ? user[item.id] : '';

						if(field_props?.auto_url) {
							return (
								<AutoPostFileUploader
									file_type={item.mediatype}
									_accepted_formats={accepted_formats}
									dd_size='full_w'
									display_custom_dragging_instructions={custom_upload_msg}
									{...field_props}
								>{item.title}</AutoPostFileUploader>
							)
						}

						return (
							<FileUploader
								allowed={item.mediatype}
								change={fileUpload}
								_accepted_formats={accepted_formats}
								dd_size='full_w'
								display_custom_dragging_instructions={custom_upload_msg}
								{...field_props}
							>{item.title}</FileUploader>
						)

					case 'signature':
						return (
							<SignatureCanvas
								{...field_props}
								display_error_at_bottom={true}
							>{item.title}</SignatureCanvas>
						)
					case 'multi_checkbox':
						return (
							<CheckBoxGroup
								{...field_props}
								elements={(isNotEmpty(item, 'values')) ? item.values.map((i) => ({ label:i.v, value:i.k })) : []}
								display_error_at_bottom={true}
							>{item.title}</CheckBoxGroup>
						)

					default:
						break;
				}
			})}
		</Model>
	)
});

export default DynamicFormBuilder;